// Step 23 — contract variables + per-variable provenance.
//
// Reads `detail.variables` exactly as `contract_detail_dict` returned
// it (list of {tag_name, value, provenance: {source_type, source_id,
// label}}). Never invents a value; unresolved variables render an
// explicit "Ej fastställt" placeholder. Missing provenance renders
// the standard "Ingen provenienskedja finns registrerad för denna
// observation." — matches Step 19's AIEvidencePanel wording.

import { AIEmpty, AISection } from "../AIPrimitives";
import { normalizeContractVariables } from "./helpers";

const SOURCE_TYPE_LABEL = {
  workspace: "Workspace",
  owner_questionnaire: "Ägarformulär",
  im_document: "IM-dokument",
  dataroom: "Dataroom",
  contracts: "Kontraktsmallar",
  bolagsverket: "Bolagsverket",
  annual_report: "Årsredovisning",
  user_company: "Bolagsinformation",
  inbound_message: "Inkommande meddelande",
  contract_draft: "Kontraktsutkast",
};

function _formatProvenance(prov) {
  if (!prov || typeof prov !== "object") return null;
  const parts = [];
  const type = prov.source_type;
  if (type) parts.push(SOURCE_TYPE_LABEL[type] || type);
  if (prov.label && prov.label !== type) parts.push(prov.label);
  if (prov.source_id) parts.push(`#${prov.source_id}`);
  return parts.length ? parts.join(" · ") : null;
}

export default function AIContractVariables({ detail }) {
  const variables = normalizeContractVariables(detail?.variables);
  if (variables.length === 0) {
    return (
      <AISection title="Variabler">
        <AIEmpty
          title="Inga variabler registrerade"
          hint="AI-drafting emitterar variabler för varje {{mustache}}-tagg i kroppen."
        />
      </AISection>
    );
  }

  const resolved = variables.filter((v) => v.is_resolved).length;
  const withProv = variables.filter((v) => v.provenance).length;

  return (
    <AISection
      title="Variabler"
      right={
        <span className="qai-page-section-hint">
          {resolved}/{variables.length} med värde · {withProv}/{variables.length} med källa
        </span>
      }
    >
      <ul className="qai-contract-variables">
        {variables.map((v) => {
          const provText = _formatProvenance(v.provenance);
          return (
            <li
              key={v.tag_name || v.display_value}
              className="qai-contract-variable-row"
              data-resolved={v.is_resolved ? "yes" : "no"}
              data-provenance={v.provenance ? "yes" : "no"}
            >
              <div className="qai-contract-variable-tag">
                {v.tag_name || <em>(namnlös)</em>}
              </div>
              <div className="qai-contract-variable-value">
                {v.is_resolved ? (
                  v.display_value
                ) : (
                  <span className="qai-contract-variable-missing">
                    Ej fastställt
                  </span>
                )}
              </div>
              <div className="qai-contract-variable-prov">
                {provText ? (
                  provText
                ) : (
                  <span className="qai-contract-variable-no-prov">
                    Ingen provenienskedja finns registrerad för denna observation.
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </AISection>
  );
}
